import React, { useCallback, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TableRow from "@mui/material/TableRow";

import { format } from "date-fns";
import truncate from "lodash/truncate";

import { misc } from "app/themes/colors";
import UploadingStatus from "features/medical-scribe/components/uploading-status.component";
import { getMedicalScribeText } from "features/medical-scribe/medical-scribes-section/scribes-list/utils";
import { ReportTableCell } from "features/reports/components/report-table-cell/report-table-cell.component";
import type { AmbientScribe } from "services/types/ambient-scribe.type";
import { convertHtmlToText } from "utils/common";

import ScribeListRowChips from "./scribe-list-row-chips.component";
import ScribeListRowPreview from "./scribe-list-row-preview.component";

interface ScribeListRowProps {
  row: AmbientScribe;
  selected: AmbientScribe | null | undefined;
  dSearch: string;
  onSelect: (row: AmbientScribe | null) => void;
  onEdit?: (row: AmbientScribe) => void;
}

function getPreviewText(row: AmbientScribe) {
  if (row.preview) {
    return {
      isSearch: true,
      text: ""
    };
  }

  const text =
    [
      convertHtmlToText(row.summary),
      convertHtmlToText(row.soap_notes),
      getMedicalScribeText(row)
    ].find(Boolean) || "";

  return {
    text: truncate(text, {
      length: 512,
      omission: "…"
    }),
    isSearch: false
  };
}

// With a preview row underneath, the main row's cells drop their bottom padding and border so
// the two read as one record; the preview row draws the border instead.
function getMainCellsSx(showPreview: boolean) {
  const alignContent = "flex-start";
  return {
    date: {
      fontWeight: 700,
      paddingBottom: showPreview ? 0 : undefined,
      borderBottom: showPreview ? undefined : "solid 2px rgba(0,0,0, 0.15)",
      alignContent
    },
    duration: {
      paddingBottom: showPreview ? 0 : undefined,
      fontWeight: 700,
      borderBottom: showPreview ? undefined : "solid 2px rgba(0,0,0, 0.2)",
      alignContent
    },
    chips: {
      borderBottom: showPreview ? undefined : "solid 2px rgba(0,0,0, 0.2)",
      alignContent
    }
  };
}

export default function ScribeListRow({
  row,
  selected,
  onSelect,
  onEdit,
  dSearch
}: ScribeListRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const previewText = useMemo(() => getPreviewText(row), [row]);

  const showPreview = Boolean(previewText.text || previewText.isSearch);
  const isSelected = row.id === selected?.id;
  const cellsSx = getMainCellsSx(showPreview);

  return (
    <React.Fragment>
      <TableRow
        className="table-row-group"
        sx={{
          boxShadow: showPreview ? "none" : undefined,
          background: isSelected ? misc.selectedSubMenuItem : undefined
        }}
        onClick={() => {
          onSelect(row);
        }}
      >
        <ReportTableCell sx={cellsSx.date}>
          <Stack>
            <Box whiteSpace="nowrap">
              {row.created_at
                ? format(new Date(row.created_at), "MM/dd/yyyy (h:mmaaa)")
                : ""}
            </Box>
            <Box py={0.5}>
              <UploadingStatus status={row.status} />
            </Box>
          </Stack>
        </ReportTableCell>
        <ReportTableCell sx={cellsSx.duration}>{row.duration}</ReportTableCell>
        <ReportTableCell sx={cellsSx.chips}>
          <ScribeListRowChips row={row} onEdit={onEdit} />
        </ReportTableCell>
      </TableRow>
      {showPreview && (
        <ScribeListRowPreview
          row={row}
          dSearch={dSearch}
          previewText={previewText.text}
          isSelected={isSelected}
          isExpanded={isExpanded}
          onToggleExpanded={toggleExpanded}
          onSelect={onSelect}
        />
      )}
    </React.Fragment>
  );
}
